/**
 * Reading a typed amount, whatever separators the keyboard offers.
 *
 * One rule for every currency: a final `.` or `,` followed by one or two
 * digits is the decimal point; every other separator groups thousands. So a
 * Colombian `66.000` is 66000, `12,50` is 12.5, and a COP interest credit of
 * `5.52` keeps its cents. Three-decimal amounts do not occur in the app's
 * currencies, which is what lets `1.234` mean 1234.
 *
 * The typed text stays the source of truth: the field shows it as typed while
 * focused, `groupForDisplay` on blur, and `ungroup` on focus again. A field
 * seeded from a stored number starts from `amountToText`, never `String(n)`:
 * `986.9123` would read as thousands.
 */

const SEPARATOR = /[.,]/;

/**
 * Split typed text into its integer digits and decimal part, or null when it
 * is not an amount. `decimalSep` is the separator the text used as a decimal
 * point (a trailing one with no digits yet counts: `12.` is being typed).
 */
function split(text) {
  if (text == null) return null;
  // A pasted `$1,234.50` or `COP 66.000` is still an amount: currency symbols,
  // a currency code at either end and spaces (NBSP included) are dropped.
  // A minus sign is not, so a negative amount is refused.
  const s = String(text)
    .replace(/[\s\p{Sc}]/gu, '')
    .replace(/^[A-Z]{3}|[A-Z]{3}$/g, '');
  if (!s || !/^[0-9.,]+$/.test(s) || !/[0-9]/.test(s)) return null;

  let intPart = s;
  let fraction = null;
  let decimalSep = null;
  const last = Math.max(s.lastIndexOf('.'), s.lastIndexOf(','));
  if (last >= 0) {
    const after = s.slice(last + 1);
    if (after.length <= 2) {
      intPart = s.slice(0, last);
      fraction = after;
      decimalSep = s[last];
    }
  }
  const digits = intPart.split(SEPARATOR).join('').replace(/^0+(?=\d)/, '');
  return { digits: digits || '0', fraction, decimalSep };
}

/** The number the text stands for, or null when it is empty or not an amount. */
export function parseAmount(text) {
  const parts = split(text);
  if (!parts) return null;
  const value = parseFloat(parts.fraction ? `${parts.digits}.${parts.fraction}` : parts.digits);
  return Number.isFinite(value) ? value : null;
}

// Every character a minus sign arrives as: the ASCII hyphen, and the
// typographic minus and dashes a paste can carry.
const MINUS = /[-\u2212\u2012\u2013\uFE63\uFF0D]/g;

/**
 * Like parseAmount, but a sign is allowed: a minus before or after the number
 * (a bank statement's `200.000-`), on either side of a currency symbol or code
 * (`$-200.000`, `-COP 1.500`), or accounting parentheses (`(200.000)`).
 * Null when the rest is not an amount. For fields where a balance can be
 * negative; movement amounts stay positive and keep parseAmount.
 */
export function parseSignedAmount(text) {
  if (text == null) return null;
  let s = String(text).replace(MINUS, '-').replace(/[\s\p{Sc}]/gu, '')
    .replace(/^-?[A-Z]{3}|[A-Z]{3}-?$/g, (code) => code.replace(/[A-Z]{3}/, ''));
  let negative = false;
  if (/^\(.*\)$/.test(s)) {
    negative = true;
    s = s.slice(1, -1);
  }
  const signs = (s.match(/-/g) || []).length;
  if (signs > 1 || (signs === 1 && !/^-|-$/.test(s))) return null;
  if (signs === 1) {
    // A minus inside accounting parentheses is two marks for one sign.
    if (negative) return null;
    negative = true;
    s = s.replace('-', '');
  }
  const value = parseAmount(s);
  if (value == null) return null;
  return negative && value !== 0 ? -value : value;
}

/**
 * The text as shown when the field is not focused: thousands grouped with `,`
 * and the decimals, as typed, after a `.` (the US layout the app shows in both
 * languages). Text that is not an amount is returned unchanged.
 */
export function groupForDisplay(text) {
  const parts = split(text);
  if (!parts) return text == null ? '' : String(text);
  const grouped = parts.digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.fraction ? `${grouped}.${parts.fraction}` : grouped;
}

/**
 * The text as edited once the field is focused again: the thousands
 * separators removed, the decimal separator kept as it was, so a backspace
 * never meets a separator the user did not type.
 *
 * Pass the typed text the field keeps, never what `groupForDisplay` showed:
 * the display always uses `.`, so `ungroup(groupForDisplay('12,50'))` is
 * `12.50`, a separator a comma-decimal keypad never typed.
 */
export function ungroup(text) {
  const parts = split(text);
  if (!parts) return text == null ? '' : String(text);
  return parts.decimalSep ? `${parts.digits}${parts.decimalSep}${parts.fraction}` : parts.digits;
}

/**
 * A stored number as field text that `parseAmount` reads back as the same
 * amount: at most two decimals, always after a `.`, and no trailing zeros.
 * `String(986.9123)` would read as 9869123, since three digits after the last
 * separator mean thousands. Empty for anything that is not a finite number.
 */
export function amountToText(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '';
  return n.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
}
